import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { Link } from "react-router-dom";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Search, Settings, CheckCircle2, BookOpen, BarChart3, TrendingUp, Sparkles } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    title: "Discover & Research",
    desc: "We gather comprehensive insights through our detailed onboarding process and strategic research methodologies.",
    icon: Search,
    color: "#FFD700",
    delay: 0,
  },
  {
    title: "Campaign Architecture",
    desc: "Our elite specialists collaborate with you to engineer bespoke marketing campaigns tailored to your vision.",
    icon: Settings,
    color: "#00D9FF",
    delay: 0.2,
  },
  {
    title: "Pre-Launch Excellence",
    desc: "We meticulously prepare and finalize all strategic elements before deployment, ensuring perfection.",
    icon: CheckCircle2,
    color: "#9333EA",
    delay: 0.4,
  },
  {
    title: "Intelligence Gathering",
    desc: "We analyze, observe, and decode audience behavior patterns to optimize performance continuously.",
    icon: BookOpen,
    color: "#FFD700",
    delay: 0.6,
  },
  {
    title: "Performance Optimization",
    desc: "We implement advanced improvements and refinements to ensure maximum campaign effectiveness.",
    icon: BarChart3,
    color: "#00D9FF",
    delay: 0.8,
  },
  {
    title: "Scale & Ascension",
    desc: "We identify and capitalize on growth opportunities to maximize ROI through strategic scaling.",
    icon: TrendingUp,
    color: "#9333EA",
    delay: 1.0,
  },
];

const ProcessSection = () => {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);
  const titleRef = useRef(null);
  const badgeRef = useRef(null);
  const orbsRef = useRef([]);

  useEffect(() => {
    const section = sectionRef.current;
    const cards = cardsRef.current;
    const title = titleRef.current;
    const badge = badgeRef.current;
    const orbs = orbsRef.current;

    // Floating orbs animation
    orbs.forEach((orb, index) => {
      gsap.to(orb, {
        y: index % 2 === 0 ? -40 : 40,
        x: index % 2 === 0 ? 20 : -20,
        rotation: 360,
        duration: 6 + index,
        repeat: -1,
        yoyo: true,
        ease: "power2.inOut",
      });
    });

    // Badge animation
    gsap.fromTo(badge,
      { opacity: 0, scale: 0.8, y: 30 },
      {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: section,
          start: "top 85%",
        }
      }
    );

    // Title animation
    gsap.fromTo(title,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        delay: 0.3,
        ease: "power3.out",
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
        }
      }
    );

    // Cards stagger animation
    gsap.fromTo(cards,
      { 
        opacity: 0, 
        y: 100, 
        scale: 0.8,
        rotationX: 45,
      },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        rotationX: 0,
        duration: 1,
        stagger: 0.15,
        delay: 0.6,
        ease: "power3.out",
        scrollTrigger: {
          trigger: cards[0],
          start: "top 85%",
        }
      }
    );

    // Card hover animations
    cards.forEach((card, index) => {
      if (card) {
        const handleMouseEnter = () => {
          gsap.to(card, {
            y: -15,
            scale: 1.03,
            rotationY: 5,
            duration: 0.4,
            ease: "power2.out"
          });
          
          gsap.to(card.querySelector('.card-glow'), {
            opacity: 1,
            scale: 1.1,
            duration: 0.4,
            ease: "power2.out"
          });
        };

        const handleMouseLeave = () => {
          gsap.to(card, {
            y: 0,
            scale: 1,
            rotationY: 0,
            duration: 0.4,
            ease: "power2.out"
          });
          
          gsap.to(card.querySelector('.card-glow'), {
            opacity: 0,
            scale: 1,
            duration: 0.4,
            ease: "power2.out"
          });
        };

        card.addEventListener('mouseenter', handleMouseEnter);
        card.addEventListener('mouseleave', handleMouseLeave);
      }
    });

  }, []);

  return (
    <section
      ref={sectionRef}
      style={{
        position: "relative",
        background: "linear-gradient(180deg, #000 0%, #0a0a0a 50%, #000 100%)",
        padding: "8rem 2rem",
        overflow: "hidden",
        color: "#fff",
      }}
    >
      {/* Background Orbs */}
      <div
        ref={(el) => orbsRef.current[0] = el}
        style={{
          position: "absolute",
          width: "500px",
          height: "500px",
          background: "radial-gradient(circle, rgba(255, 215, 0, 0.1) 0%, transparent 70%)",
          borderRadius: "50%",
          top: "10%",
          right: "-15%",
          filter: "blur(100px)",
          zIndex: 1,
        }}
      />
      <div
        ref={(el) => orbsRef.current[1] = el}
        style={{
          position: "absolute",
          width: "400px",
          height: "400px",
          background: "radial-gradient(circle, rgba(0, 217, 255, 0.08) 0%, transparent 70%)",
          borderRadius: "50%",
          bottom: "20%",
          left: "-10%",
          filter: "blur(80px)",
          zIndex: 1,
        }}
      />
      <div
        ref={(el) => orbsRef.current[2] = el}
        style={{
          position: "absolute",
          width: "300px",
          height: "300px",
          background: "radial-gradient(circle, rgba(147, 51, 234, 0.06) 0%, transparent 70%)",
          borderRadius: "50%",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          filter: "blur(60px)",
          zIndex: 1,
        }}
      />

      {/* Floating Particles */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `
            radial-gradient(2px 2px at 20px 30px, rgba(255, 255, 255, 0.1), transparent),
            radial-gradient(2px 2px at 40px 70px, rgba(255, 215, 0, 0.1), transparent),
            radial-gradient(1px 1px at 90px 40px, rgba(0, 217, 255, 0.1), transparent),
            radial-gradient(1px 1px at 130px 80px, rgba(147, 51, 234, 0.1), transparent)
          `,
          backgroundRepeat: "repeat",
          backgroundSize: "150px 100px",
          animation: "particleFloat 20s linear infinite",
          zIndex: 1,
        }}
      />

      <div style={{ maxWidth: "1400px", margin: "0 auto", position: "relative", zIndex: 10 }}>
        {/* Section Badge */}
        <div
          ref={badgeRef}
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "3rem",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.75rem 1.5rem",
              background: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 215, 0, 0.4)",
              borderRadius: "50px",
              fontSize: "0.95rem",
              fontWeight: "600",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(255, 215, 0, 0.15)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: "-50%",
                width: "50%",
                height: "100%",
                background: "linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.5), transparent)",
                transform: "skewX(-20deg)",
                animation: "shimmer 3s infinite",
              }}
            />
            <Sparkles size={18} style={{ color: "#FFD700" }} />
            Excellence Framework
          </div>
        </div>

        {/* Title Section */}
        <div
          ref={titleRef}
          style={{
            textAlign: "center",
            marginBottom: "6rem",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "1.5rem",
              color: "#fff",
            }}
          >
            From{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #FFD700, #00D9FF, #9333EA)",
                backgroundSize: "200% 100%",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                animation: "gradientShift 3s ease-in-out infinite",
              }}
            >
              Discovery
            </span>{" "}
            To{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #9333EA, #FFD700, #00D9FF)",
                backgroundSize: "200% 100%",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                animation: "gradientShift 3s ease-in-out infinite reverse",
              }}
            >
              Destiny
            </span>
          </h2>
          
          <p
            style={{
              fontSize: "clamp(1rem, 1.5vw, 1.2rem)",
              color: "rgba(255, 255, 255, 0.8)",
              lineHeight: "1.7",
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            Our battle-tested 6-phase methodology that transforms ambitious visions into extraordinary results.
          </p>
        </div>

        {/* Process Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
            gap: "2.5rem",
            perspective: "1000px",
          }}
        >
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div
                key={index}
                ref={(el) => (cardsRef.current[index] = el)}
                style={{
                  position: "relative",
                  background: "rgba(255, 255, 255, 0.02)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "24px",
                  padding: "2.5rem",
                  cursor: "pointer",
                  transformStyle: "preserve-3d",
                  overflow: "hidden",
                }}
              >
                {/* Card Glow Effect */}
                <div
                  className="card-glow"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(135deg, ${step.color}20, transparent)`,
                    borderRadius: "24px",
                    opacity: 0,
                    transition: "opacity 0.4s ease",
                  }}
                />

                {/* Top Border Shine */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "1px",
                    background: `linear-gradient(90deg, transparent, ${step.color}80, transparent)`,
                    transform: "translateX(-100%)",
                    animation: `borderShine 3s infinite ${step.delay}s`,
                  }}
                />

                {/* Phase Number */}
                <div
                  style={{
                    position: "absolute",
                    top: "1.5rem",
                    right: "1.5rem",
                    width: "40px",
                    height: "40px",
                    background: `linear-gradient(135deg, ${step.color}, ${step.color}80)`,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#000",
                    fontWeight: "800",
                    fontSize: "1rem",
                  }}
                >
                  {index + 1}
                </div>

                {/* Icon */}
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background: `linear-gradient(135deg, ${step.color}, ${step.color}80)`,
                    borderRadius: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "2rem",
                    color: "#000",
                    boxShadow: `0 8px 25px ${step.color}40`,
                  }}
                >
                  <IconComponent size={32} />
                </div>

                {/* Content */}
                <div style={{ position: "relative", zIndex: 10 }}>
                  <h3
                    style={{
                      fontSize: "1.4rem",
                      fontWeight: "700",
                      marginBottom: "1rem",
                      color: "#fff",
                      lineHeight: "1.3",
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    style={{
                      color: "rgba(255, 255, 255, 0.7)",
                      lineHeight: "1.7",
                      fontSize: "1rem",
                    }}
                  >
                    {step.desc}
                  </p>
                </div>

                {/* Bottom Accent */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "60px",
                    height: "3px",
                    background: `linear-gradient(90deg, transparent, ${step.color}, transparent)`,
                    borderRadius: "2px",
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div
          style={{
            textAlign: "center",
            marginTop: "6rem",
            padding: "3rem",
            background: "rgba(255, 255, 255, 0.01)",
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            border: "1px solid rgba(255, 255, 255, 0.05)",
          }}
        >
          <h3
            style={{
              fontSize: "2rem",
              fontWeight: "700",
              marginBottom: "1rem",
              background: "linear-gradient(135deg, #FFD700, #00D9FF)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            Ready to Begin Your Journey?
          </h3>
          <p
            style={{
              color: "rgba(255, 255, 255, 0.7)",
              marginBottom: "2rem",
              fontSize: "1.1rem",
            }}
          >
            Let our proven framework transform your business goals into extraordinary achievements.
          </p>
               <Link to="/consultation">
          <button
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "1.2rem 2.5rem",
              background: "linear-gradient(135deg, #FFD700, #00D9FF)",
              color: "#000",
              border: "none",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: "700",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 15px 40px rgba(255, 215, 0, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Start Your Ascension <TrendingUp size={20} />
          </button>
          </Link>
        </div>
      </div>

      <style jsx>{`
        @keyframes particleFloat {
          0% { transform: translateY(0px); }
          100% { transform: translateY(-100px); }
        }
        
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes shimmer {
          0% { left: -50%; }
          100% { left: 120%; }
        }
        
        @keyframes borderShine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        @media (max-width: 768px) {
          section {
            padding: 4rem 1rem !important;
          }
        }
      `}</style>
    </section>
  );
};

export default ProcessSection;